import { ethers } from 'ethers';

const SOGNI_API_ENDPOINT = process.env.REACT_APP_SOGNI_API_ENDPOINT || '';
const TSOGNI_TOKEN_NAME = 'Sogni AI';
const TSOGNI_TOKEN_VERSION = '1';
const VERIFYING_CONTRACT_ADDRESS = '0xCcCCccccCCCCcCCCCCCcCcCccCcCCCcCcccccccC';

export async function getNonce(walletAddress) {
  const response = await fetch(`${SOGNI_API_ENDPOINT}/v1/account/nonce`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ walletAddress })
  });

  if (!response.ok) {
    throw new Error(`Failed to get nonce: ${response.status}`);
  }

  const data = await response.json();
  return data.data.nonce;
}

export async function getPermitData(walletAddress, amount) {
  const response = await fetch(`${SOGNI_API_ENDPOINT}/v1/account/token/deposit/permit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      walletAddress,
      amount: ethers.utils.parseEther(amount).toString()
    })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export async function generatePermitSignature(signer, permitData) {
  const permitSignature = await signer._signTypedData(
    permitData.data.domain,
    permitData.data.types,
    permitData.data.message
  );
  return ethers.utils.splitSignature(permitSignature);
}

export async function createDepositTokenData(account, depositAmount, deadline, sig, nonce, chainId) {
  const domain = {
    name: TSOGNI_TOKEN_NAME,
    version: TSOGNI_TOKEN_VERSION,
    chainId,
    verifyingContract: VERIFYING_CONTRACT_ADDRESS
  };

  const types = {
    DepositToken: [
      { name: 'walletAddress', type: 'address' },
      { name: 'amount', type: 'string' },
      { name: 'deadline', type: 'uint256' },
      { name: 'v', type: 'uint8' },
      { name: 'r', type: 'bytes32' },
      { name: 's', type: 'bytes32' },
      { name: 'nonce', type: 'string' },
    ]
  };

  const value = {
    walletAddress: account,
    amount: ethers.utils.parseEther(depositAmount).toString(),
    deadline,
    v: sig.v,
    r: sig.r,
    s: sig.s,
    nonce
  };

  return { domain, types, value };
}

export async function generateDepositSignature(signer, depositTokenData) {
  return signer._signTypedData(
    depositTokenData.domain,
    depositTokenData.types,
    depositTokenData.value
  );
}

export async function submitDeposit(depositData) {
  const response = await fetch(`${SOGNI_API_ENDPOINT}/v3/account/token/deposit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(depositData)
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Deposit failed');
  }

  return response.json();
}

export async function processDeposit(signer, account, depositAmount, chainId) {
  // Get nonce
  const nonce = await getNonce(account);

  // Get permit data
  const permitData = await getPermitData(account, depositAmount);
  
  // Generate permit signature
  const sig = await generatePermitSignature(signer, permitData);
  
  // Create deposit token data
  const deadline = permitData.data.message.deadline;
  const depositTokenData = await createDepositTokenData(
    account, 
    depositAmount, 
    deadline, 
    sig, 
    nonce, 
    chainId
  );

  // Generate final signature
  const signature = await generateDepositSignature(signer, depositTokenData);

  // Submit deposit
  const depositData = {
    walletAddress: account,
    amount: ethers.utils.parseEther(depositAmount).toString(),
    deadline: deadline,
    v: sig.v,
    r: sig.r,
    s: sig.s,
    signature: signature
  };

  const result = await submitDeposit(depositData);
  console.log('Deposit response:', result);
  return result;
}

export async function createWithdrawTokenData(account, withdrawAmount, chainId, nonce) {
  const domain = {
    name: TSOGNI_TOKEN_NAME,
    version: TSOGNI_TOKEN_VERSION,
    chainId,
    verifyingContract: VERIFYING_CONTRACT_ADDRESS
  };

  const types = {
    WithdrawToken: [
      { name: 'walletAddress', type: 'address' },
      { name: 'amount', type: 'string' },
      { name: 'nonce', type: 'string' },
    ]
  };

  const value = {
    walletAddress: account,
    amount: ethers.utils.parseEther(withdrawAmount).toString(),
    nonce
  };

  return { domain, types, value };
}

export async function generateWithdrawSignature(signer, withdrawTokenData) {
  return signer._signTypedData(
    withdrawTokenData.domain,
    withdrawTokenData.types,
    withdrawTokenData.value
  );
}

export async function submitWithdraw(withdrawData) {
  const response = await fetch(`${SOGNI_API_ENDPOINT}/v1/account/token/withdraw`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(withdrawData)
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Withdraw failed');
  }

  return response.json();
}

export async function processWithdraw(signer, account, withdrawAmount, chainId) {
  // Create withdraw token data
  const nonce = await getNonce(account);
  const withdrawTokenData = await createWithdrawTokenData(
    account,
    withdrawAmount,
    chainId,
    nonce
  );

  // Generate signature
  const signature = await generateWithdrawSignature(signer, withdrawTokenData);
  // Submit withdraw
  const withdrawData = {
    walletAddress: account,
    amount: ethers.utils.parseEther(withdrawAmount).toString(),
    signature: signature
  };


  const result = await submitWithdraw(withdrawData);
  return result;
}

export async function createWorkerWithdrawTokenData(account, withdrawAmount, destination, chainId, nonce) {
  const domain = {
    name: TSOGNI_TOKEN_NAME,
    version: TSOGNI_TOKEN_VERSION,
    chainId,
    verifyingContract: VERIFYING_CONTRACT_ADDRESS
  };

  const types = {
    WithdrawTokenWorker: [
      { name: 'walletAddress', type: 'address' },
      { name: 'amount', type: 'string' },
      { name: 'destination', type: 'string' },
      { name: 'nonce', type: 'string' },
    ]
  };

  const value = {
    walletAddress: account,
    amount: ethers.utils.parseEther(withdrawAmount).toString(),
    destination,
    nonce
  };

  return { domain, types, value };
}

export async function generateWorkerWithdrawSignature(signer, withdrawTokenData) {
  return signer._signTypedData(
    withdrawTokenData.domain,
    withdrawTokenData.types,
    withdrawTokenData.value
  );
}

export async function submitWorkerWithdraw(withdrawData) {
  const response = await fetch(`${SOGNI_API_ENDPOINT}/v1/account/token/worker/claim`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(withdrawData)
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Worker withdraw failed');
  }

  return response.json();
}

export async function processWorkerWithdraw(signer, account, withdrawAmount, destination, chainId) {
  // Get nonce
  const nonce = await getNonce(account);
  
  // Create worker withdraw token data
  const withdrawTokenData = await createWorkerWithdrawTokenData(
    account,
    withdrawAmount,
    destination,
    chainId,
    nonce
  );

  // Generate signature
  const signature = await generateWorkerWithdrawSignature(signer, withdrawTokenData);

  // Submit worker withdraw
  const withdrawData = {
    walletAddress: account,
    amount: ethers.utils.parseEther(withdrawAmount).toString(),
    destination,
    signature
  };

  const result = await submitWorkerWithdraw(withdrawData);
  return result;
}
